import { FC, useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { MainPage, PageTitle } from '../../components'
import ShipmentBulkUploadReview from './ShipmentBulkUploadReview'
import { useAppSelector, useAppDispatch } from '../../store/hooks'
import { hydrateBulkCatalogs } from '../../store/slices/bulkCatalogs'
import { getImport } from '../../services/ShipmentBulkUploadServices'
import { getFavoriteAndRecentDestinationsDjango } from '../../services/AccountServices'
import {
  getBulkActionFlags,
  headerStatusMeta,
  isClosedHeaderStatus,
  mapDetailExecutionError,
  messageForCode,
  minDeclaredValueFromExecutionError,
  resolveDetailStatusName,
} from '../../utils/shipmentBulkUploadStatus'
import { payloadJsonToShipmentGroup } from '../../utils/shipmentPayload'
import { isJuridicDocType } from '../../utils/shipmentValidation'
import { ShipmentGroup } from '../../interfaces/Import/ShipmentGroupInterface'
import { DestinationInterface } from '../../interfaces/Account/DestinationInterface'
import { ImportDetailStatusName } from '../../interfaces/Import/ShipmentBulkUploadApi'
import {
  BULK_UPLOAD_DENIED_MESSAGE,
  ownsBulkUpload,
} from '../../utils/bulkUploadOwnership'

const LIST_ROUTE = '/shipment/bulk-upload/list'

/**
 * Vista de corrección (Approach A, eng-review E2): toma los detalles en estado Error de
 * una importación, reconstruye sus ShipmentGroup desde payload_json y los abre en el
 * editor (Review) en modo submitMode='updateImport'. Al guardar, el Review hace
 * PUT /update con las guías corregidas y vuelve al detalle.
 */

/**
 * Restaura el favorito en grupos reconstruidos: matchea el RIF del destinatario
 * (consigneeDocNumber) contra la lista de favoritos de la cuenta y, al encontrarlo,
 * setea favoriteDestinationId (y el punto de referencia si es a domicilio). Muta los
 * grupos en sitio. No pisa datos del payload — solo agrega lo que el payload no traía.
 */
function applyFavorites(
  groups: ShipmentGroup[],
  favorites: DestinationInterface[]
): void {
  if (!favorites.length) return
  for (const g of groups) {
    const rif = (g.consigneeDocNumber || '').trim()
    if (!rif) continue
    const fav = favorites.find(
      (f) => (f.account?.identificationNumber || '').trim() === rif
    )
    if (!fav) continue
    g.favoriteDestinationId = fav.id || fav.account?.id || ''
    // Punto de referencia: solo aplica a domicilio y solo si el grupo no lo trae ya.
    if (fav.isHomeDelivery && !g.addressReference && fav.location?.reference) {
      g.addressReference = fav.location.reference
    }
  }
}

const ShipmentBulkUploadFix: FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const headerId = id ?? ''

  const dispatch = useAppDispatch()
  const user = useAppSelector((s) => s.user)
  // Carga masiva: tiendas, ciudades, categorias y la relacion ciudad→tienda del sub-slice
  // Django (fresco por sesion). identificationTypes sigue en .NET (inmutable).
  const businessUnits = useAppSelector((s) => s.bulkCatalogs.businessUnits)
  const identificationTypes = useAppSelector(
    (s) => s.inmutable.taxIdentificationTypes
  )
  const citiesType = useAppSelector((s) => s.bulkCatalogs.cities)
  const citiesByBu = useAppSelector((s) => s.bulkCatalogs.citiesByBu)
  const pieceCategories = useAppSelector((s) => s.bulkCatalogs.pieceCategories)

  // Fix es navegable directamente (/.../fix): hidrata los catalogos Django si aun no lo estan.
  useEffect(() => {
    dispatch(hydrateBulkCatalogs())
  }, [dispatch])

  const isCOD =
    user.paymentMethod?.some((m: any) => m.paymentModeID?.toString() === '2') ??
    false

  const [groups, setGroups] = useState<ShipmentGroup[]>([])
  /** Errores por fila/campo traducidos del `error_message` de cada detalle. */
  const [initialErrors, setInitialErrors] = useState<
    Record<number, Record<string, string>>
  >({})
  /** Mínimos de valor declarado por fila, derivados del error del backend. */
  const [initialMins, setInitialMins] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    if (!headerId) return
    setLoading(true)
    getImport(headerId).then(async (res) => {
      if (cancelled) return
      if (res.didError || !res.model) {
        setLoading(false)
        setError(messageForCode(res.code, res.errorMessage))
        return
      }
      /**
       * Carga de otra línea de facturación: misma guarda que el detalle. Esta
       * ruta también es pegable, así que sin esto el bloqueo del detalle se
       * saltearía escribiendo `/fix` en la URL.
       */
      if (!ownsBulkUpload(res.model, user.client)) {
        setLoading(false)
        navigate(LIST_ROUTE, {
          replace: true,
          state: { deniedMessage: BULK_UPLOAD_DENIED_MESSAGE },
        })
        return
      }
      /**
       * Cabecera cerrada (Finalizado / Cancelado / Eliminado): el detalle ya no
       * ofrece el botón, pero esta ruta es navegable a mano y queda en el
       * historial del navegador. Sin esta guarda el operador llegaba al editor,
       * corregía las guías y recién al guardar el backend le decía que no.
       */
      if (isClosedHeaderStatus(res.model.status_name, res.model.status_id)) {
        setLoading(false)
        setError(
          `Esta carga masiva está en estado "${headerStatusMeta(
            res.model.status_name,
            res.model.status_id
          ).label}" y ya no admite correcciones.`
        )
        return
      }
      /**
       * Carga todavía en curso: mismo criterio que la guarda de arriba. El
       * detalle ya no ofrece el botón hasta que se procesa el 100%, pero esta
       * ruta se puede tipear o volver a ella desde el historial. Corregir a
       * medias no sirve: las líneas que faltan pueden sumar errores nuevos, y el
       * editor se abriría con un subconjunto que cambia bajo los pies.
       */
      const flags = getBulkActionFlags(
        res.model.details,
        res.model.status_name,
        res.model.status_id
      )
      if (!flags.isFullyProcessed) {
        setLoading(false)
        const faltan = flags.pendingCount + flags.runningCount
        setError(
          `Esta carga masiva todavía se está procesando (faltan ${faltan} de ${res.model.details.length} guías). ` +
            'Podés corregir los errores cuando termine.'
        )
        return
      }
      // Errores de dato que el usuario corrige vía corrections[]: 75 (Error) y 77
      // (Error procesando). NO se traen 78 (Error servidor, se reintenta) ni 76 (Sustituida).
      const errorDetails = res.model.details.filter((d) => {
        const s = resolveDetailStatusName(d.status_name, d.status_id)
        return (
          s === ImportDetailStatusName.Error ||
          s === ImportDetailStatusName.ErrorProcessing
        )
      })
      let reconstructed: ShipmentGroup[]
      try {
        reconstructed = errorDetails.map((d) => ({
          ...payloadJsonToShipmentGroup(d.payload_json, {
            identificationTypes,
            isCOD,
          }),
          // Arrastramos el id del detalle en error para el PUT /update por corrections[].
          importDetailId: d.shipment_bulk_upload_detail_id,
        }))
      } catch (e: any) {
        if (cancelled) return
        setLoading(false)
        setError(
          'No se pudieron reconstruir las guías con error: ' + (e?.message || '')
        )
        return
      }

      /**
       * Errores por campo derivados del `error_message` de cada detalle.
       *
       * Sin esto, el operador entra a Corregir errores y ve N guías idénticas sin
       * saber cuál falló ni por qué: el motivo solo estaba en la tabla del
       * detalle, en la pantalla anterior. Traducirlo acá lo ancla al campo y a la
       * guía que corresponde.
       *
       * Va DESPUÉS de reconstruir: varios mensajes del backend nombran el valor
       * rechazado pero no el campo (el mismo `account.Account.1` puede ser el
       * nombre del remitente o el del destinatario), y sin la guía a mano el
       * traductor no puede decidir cuál marcar.
       */
      const initialServerErrors: Record<number, Record<string, string>> = {}
      /** Mínimo de valor declarado por detalle; el editor lo re-evalúa en vivo. */
      const initialMins: Record<string, number> = {}
      errorDetails.forEach((d, i) => {
        const g = reconstructed[i]
        if (!g) return
        const mapped = mapDetailExecutionError(d.error_message, {
          shipperName: g.shipperName,
          consigneeName: g.consigneeName,
          consigneePhone: g.consigneePhone,
          consigneePhoneCountryCode: g.consigneePhoneCountryCode,
          shipmentHeaderReference: g.shipmentHeaderReference,
          destinationCity: g.destinationCity,
          storeCode: g.storeCode,
          isCOD,
          // Tipo de persona: las reglas de nombre (charset y 2 palabras) son
          // distintas para jurídicos, así que el traductor las necesita para no
          // re-marcar un nombre que hoy es válido.
          shipperIsJuridic: isJuridicDocType(
            identificationTypes,
            g.shipperDocType
          ),
          consigneeIsJuridic: isJuridicDocType(
            identificationTypes,
            g.consigneeDocType
          ),
        })
        if (Object.keys(mapped).length > 0) initialServerErrors[i] = mapped
        const min = minDeclaredValueFromExecutionError(d.error_message)
        if (min != null) initialMins[d.shipment_bulk_upload_detail_id] = min
        /**
         * Lo que no se pudo anclar a un campo se muestra igual, en la guía que lo
         * causó. Antes quedaba solo en la tabla del detalle, en la pantalla
         * anterior: el operador entraba acá y veía la guía en verde, con el check
         * de "guía válida", sin rastro del motivo del rechazo.
         */
        if (d.error_message?.trim()) g.serverErrorNote = d.error_message.trim()
      })

      // El payload_json NO transporta el favorito (el backend lo re-serializa con
      // su propio modelo y descarta campos extra). Lo re-derivamos acá matcheando
      // el RIF del destinatario contra la lista de favoritos de la cuenta — igual
      // que el flujo de creación — para restaurar favoriteDestinationId y, con él,
      // todas las condiciones de UI que dependen del favorito (chip, picker,
      // skip del auto-sugerido de RIF, punto de referencia).
      const accountId = user?.client?.id
      if (accountId) {
        try {
          const favRes = await getFavoriteAndRecentDestinationsDjango(
            accountId,
            identificationTypes,
            businessUnits,
            ''
          )
          if (!cancelled && !favRes.didError && favRes.model) {
            applyFavorites(reconstructed, favRes.model.favoriteDestinations || [])
          }
        } catch {
          /* silencioso — sin favoritos solo se pierde el resaltado, no la corrección */
        }
      }

      if (cancelled) return
      setLoading(false)
      // Antes de `setGroups`: el Review solo monta cuando hay grupos, y lee estos
      // errores como estado INICIAL (una sola vez). Si llegaran después, no los vería.
      // `reconstructed` es un `map` 1:1 sobre `errorDetails`, así que los índices
      // coinciden — marcar por el índice equivocado señalaría la guía que no es.
      setInitialErrors(initialServerErrors)
      setInitialMins(initialMins)
      setGroups(reconstructed)
    })
    return () => {
      cancelled = true
    }
  }, [
    headerId,
    identificationTypes,
    isCOD,
    businessUnits,
    user?.client,
    navigate,
  ])

  const backToDetail = useCallback(() => {
    navigate(`/shipment/bulk-upload/${headerId}`)
  }, [navigate, headerId])

  return (
    <MainPage>
      <PageTitle title="Corregir guías con error" back />

      {loading ? (
        <div className="animate-pulse space-y-3">
          <div className="h-12 rounded-lg bg-gray-100" />
          <div className="h-64 rounded-lg bg-gray-100" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      ) : groups.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-10 text-center">
          <p className="text-sm font-medium text-gray-700">
            No hay guías con error para corregir
          </p>
          <button
            onClick={backToDetail}
            className="mt-3 text-sm text-main-500 hover:underline"
          >
            Volver al detalle
          </button>
        </div>
      ) : (
        <>
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
            Estás corrigiendo {groups.length} guía(s) con error. Al guardar, se
            re-enviarán para procesarse de nuevo.
          </div>
          <ShipmentBulkUploadReview
            groups={groups}
            setGroups={setGroups}
            setNext={() => {}}
            isCOD={isCOD}
            businessUnits={businessUnits}
            identificationTypes={identificationTypes}
            citiesType={citiesType}
            citiesByBu={citiesByBu}
            pieceCategories={pieceCategories}
            submitMode="updateImport"
            headerId={headerId}
            initialServerErrors={initialErrors}
            initialMinDeclaredValue={initialMins}
          />
        </>
      )}
    </MainPage>
  )
}

export default ShipmentBulkUploadFix
